import { useState } from 'react';
import { Plug } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { ApiError, apiFetch } from '@avagency/frontend/lib/api';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@avagency/frontend/components/ui/card';
import { Button } from '@avagency/frontend/components/ui/button';
import { Badge } from '@avagency/frontend/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@avagency/frontend/components/ui/table';
import { EmptyState } from '@avagency/frontend/components/shell/EmptyState';
import { ErrorState } from '@avagency/frontend/components/shell/ErrorState';
import { LoadingState } from '@avagency/frontend/components/shell/LoadingState';

interface Integration {
  id: string;
  provider: string;
  external_id: string;
  display_name: string;
  status: string;
  installed_at: string;
}

// Backend wraps the list in an envelope — see handler_integrations.go
// `IntegrationResponse{Integrations: ...}`. Don't return a bare array
// or `q.data.length` reads against undefined and the table never renders.
interface IntegrationListResponse {
  integrations: Integration[];
}

type IntegrationStatus = 'active' | 'revoked' | 'error' | 'reauth_required';
type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';

const STATUS_VARIANT: Record<IntegrationStatus, BadgeVariant> = {
  active: 'default',
  revoked: 'destructive',
  error: 'destructive',
  reauth_required: 'secondary',
};

function statusVariant(status: string): BadgeVariant {
  return STATUS_VARIANT[status as IntegrationStatus] ?? 'outline';
}

async function startStripeOAuth(): Promise<void> {
  // POST /v1/oauth/{provider}/start (platform/oauth/server/server.go:76)
  // returns { authorize_url }. return_url is REQUIRED (400 without it).
  // Navigate there; Stripe callback returns to app via return_url.
  const result = await apiFetch<{ authorize_url: string }>(
    '/v1/oauth/stripe/start',
    { method: 'POST', json: { return_url: '/' } },
  );
  window.location.href = result.authorize_url;
}

export function IntegrationsCard() {
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);

  const q = useQuery<Integration[]>({
    queryKey: ['dundial', 'integrations'],
    queryFn: async () => {
      try {
        const body = await apiFetch<IntegrationListResponse>('/api/v1/integrations');
        return body.integrations;
      } catch (err) {
        if (err instanceof ApiError && err.status === 503) {
          // OAuthStore not wired (dev / partial deploy). UI degrades
          // to empty state so first-run users still see Connect Stripe.
          return [];
        }
        throw err;
      }
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Connected accounts</CardTitle>
        <CardDescription>
          Your Stripe accounts connected via OAuth. Each one is monitored
          for declines and retry-eligible payment failures.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {q.isLoading && <LoadingState rows={2} />}
        {q.error && <ErrorState error={q.error} retry={() => q.refetch()} />}
        {q.data && q.data.length === 0 && (
          <EmptyState
            icon={
              <div className="dundial-empty-glyph" aria-hidden>
                <Plug className="h-6 w-6" style={{ color: "#b8a4f5" }} />
              </div>
            }
            title="No Stripe accounts connected yet"
            description="Connect your first Stripe account to start tuning retry policies."
          />
        )}
        {q.data && q.data.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Account</TableHead>
                <TableHead>External ID</TableHead>
                <TableHead className="text-right">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {q.data.map((i) => (
                <TableRow key={i.id}>
                  <TableCell>
                    <div className="flex items-center gap-2 font-medium">
                      {i.status === 'active' && (
                        <span className="dundial-live-dot" aria-hidden title="Active" />
                      )}
                      {i.display_name}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-[11px] text-muted-foreground/70">{i.external_id}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge
                      variant={statusVariant(i.status)}
                      className={i.status === 'active' ? 'dundial-status-recovered' : undefined}
                    >
                      {i.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <div className="space-y-2">
          {connectError && (
            <p className="text-sm text-destructive">{connectError}</p>
          )}
          <Button
            disabled={connecting}
            onClick={async () => {
              setConnecting(true);
              setConnectError(null);
              try {
                await startStripeOAuth();
              } catch (err) {
                setConnecting(false);
                setConnectError(
                  err instanceof ApiError
                    ? err.message
                    : 'Failed to start OAuth flow. Please try again.',
                );
              }
            }}
          >
            {connecting ? 'Redirecting…' : 'Connect Stripe'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
